import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getList, deleteOne, create, update } from "../../dataProvider/API";
import { ALERT_TEXT } from "../../constants/titles";

const initialState = {
  loading: false,
  fetchDataAds: null, // null is safer than {} for "not loaded yet"
  error: null,
};

export const getAds = createAsyncThunk(
  "ads/getAds",
  async ({ endpoint, params }, { rejectWithValue }) => {
    try {
      const res = await getList(endpoint, params);
      return res;
    } catch (e) {
      return rejectWithValue(e?.message || "getAds failed");
    }
  },
);

export const deleteAd = createAsyncThunk(
  "ads/deleteAd",
  async (endpoint, { rejectWithValue }) => {
    try {
      const res = await deleteOne(endpoint);
      return res;
    } catch (e) {
      return rejectWithValue(e?.message || "deleteAd failed");
    }
  },
);

export const createAd = createAsyncThunk(
  "ads/createAd",
  async ({ endpoint, data }, { rejectWithValue }) => {
    try {
      const res = await create(endpoint, data);
      return res;
    } catch (e) {
      return rejectWithValue(e?.message || "createAd failed");
    }
  },
);

export const updateAd = createAsyncThunk(
  "ads/updateAd",
  async ({ endpoint, data }, { rejectWithValue }) => {
    try {
      const res = await update(endpoint, data);
      return res;
    } catch (e) {
      return rejectWithValue(e?.message || "updateAd failed");
    }
  },
);

export const adsSlice = createSlice({
  name: "ads",
  initialState,
  reducers: {
    // optional: if you ever want to clear state safely
    resetAdsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // -------- getAds --------
      .addCase(getAds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAds.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        // Defensive: never allow slice state to become a primitive by mistake.
        // Only assign fetchDataAds if payload is an object.
        const payload = action.payload;
        state.fetchDataAds =
          payload && typeof payload === "object" ? payload : null;
      })
      .addCase(getAds.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "getAds failed";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      // -------- deleteAd --------
      .addCase(deleteAd.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAd.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success(ALERT_TEXT.SUCCESS_DELETE_AD);
      })
      .addCase(deleteAd.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "deleteAd failed";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      // -------- createAd --------
      .addCase(createAd.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAd.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success(ALERT_TEXT.SUCCESS_CREATE_AD);
      })
      .addCase(createAd.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "createAd failed";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      })

      // -------- updateAd --------
      .addCase(updateAd.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAd.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success(ALERT_TEXT.SUCCESS_UPDATE_AD);
      })
      .addCase(updateAd.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || "updateAd failed";
        toast.error(ALERT_TEXT.BAD_REQUEST);
      });
  },
});

export const { resetAdsState } = adsSlice.actions;

export default adsSlice.reducer;
